import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { FileInfoResponse } from '../../../model/src/lib/file-info-response.model';
import { FileInfo } from '../../../model/src/lib/file-info.model';
import { AppConfig } from './../../config/app.config';
import { ApiBaseService } from './api-base.service';

@Injectable({
  providedIn: 'root',
})
export class ApiFileCenterService extends ApiBaseService {
  constructor(
    protected appConfig: AppConfig,
    protected http: HttpClient,
    private logger: NGXLogger
  ) {
    super(appConfig.getApiUrl('fileCenter'), http);
  }

  /**
   * @description 上傳檔案(單檔)
   *
   * @param uploadRequest 上傳檔案相關資料,包含 domain, subDomain, creator, description, file, sortIndex
   * @returns 檔案中心記錄之檔案序號
   */
  uploadFile(uploadRequest: {
    domain: string;
    subDomain: string;
    creator: string;
    description: string;
    file: File;
    sortIndex: number;
  }): Observable<string> {
    const formData = new FormData();
    formData.append('Domain', uploadRequest.domain);
    formData.append('SubDomain', uploadRequest.subDomain);
    formData.append('Description', uploadRequest.description);
    formData.append('Sort', JSON.stringify(uploadRequest.sortIndex));
    formData.append('IsTemp', JSON.stringify(false));
    formData.append('Creator', uploadRequest.creator);
    formData.append('IFormFile', uploadRequest.file);

    // ngsw-bypass: 只要頁面被 Angular Service Worker 控制，所有 fetch 都會被攔截並在 SW
    // 內部用 scope.fetch() 重新轉發一次（與 ngsw-config.json 有無設定 dataGroups 無關）。
    // iOS WebKit 對「被 SW 轉發的 multipart/form-data streaming body」有已知缺陷，會把
    // binary 內容吃掉，造成後端收到空檔案。加此 header 讓上傳請求完全跳過 SW 攔截層。
    return this.doPost<string>(
      `/FileCenter/PostFilesUploadAsync`,
      formData,
      { 'ngsw-bypass': 'true' },
      'text' as 'text'
    );
  }

  /**
   * @description 下載檔案(單檔)
   *
   * @param downloadRequest 下載檔案相關資料,包含 fileDetailId, downloader
   * @returns 檔案 blob 或 null
   */
  getFileDownloadAsync(downloadRequest: {
    fileDetailId: string;
    downloader: string;
  }): Observable<Blob | null> {
    const headerParams = new HttpParams()
      .append('fileDetailId', downloadRequest.fileDetailId)
      .append('downloader', downloadRequest.downloader);
    return this.doGet<Blob>(
      `/FileCenter/GetFileDownloadAsync`,
      headerParams,
      {},
      'blob' as 'blob'
    ).pipe(
      catchError((error) => {
        this.logger.error(
          `ApiFileCenterService getFileDownloadAsync(${JSON.stringify(
            downloadRequest
          )}) error:`,
          error
        );
        return of(null);
      })
    );
  }

  /**
   * 取得檔案中心內指定檔案的下載連結
   *
   * @param {string} fileId 檔案 ID
   * @param {string} downloader
   * @return {*}  {string}
   * @memberof ApiFileCenterService
   */
  getFileDownloadUrl(fileId: string, downloader: string): string {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.set('fileDetailId', fileId);
    queryParameters = queryParameters.set('downloader', downloader);
    const url = `${this.baseUrl}/FileCenter/GetFileDownloadAsync?${queryParameters.toString()}`;
    return url;
  }

  /**
   * @description 下載 zip 檔案(多檔)
   *
   * @param downloadRequest 下載檔案相關資料,包含 fileDetailIds, downloader
   * @returns zip 檔案 blob 或 null
   */
  getFilesBundleDownloadAsync(downloadRequest: {
    fileDetailIds: string[];
    downloader: string;
  }): Observable<Blob | null> {
    const headerParams = new HttpParams().appendAll(downloadRequest);
    return this.doGet<Blob>(
      `/FileCenter/GetFilesBundleDownloadAsync`,
      headerParams,
      {},
      'blob' as 'blob'
    ).pipe(
      catchError((error) => {
        this.logger.error(
          `ApiFileCenterService getFilesBundleDownloadAsync(${JSON.stringify(
            downloadRequest
          )}) error:`,
          error
        );
        return of(null);
      })
    );
  }

  /**
   * @description 取得檔案資訊(單檔)
   *
   * @param fileDetailId 檔案序號
   * @param domain domain
   * @param subDomain SubDomain
   * @returns 檔案資訊
   */
  getFileDetail(
    fileDetailId: string,
    domain: string,
    subDomain: string
  ): Observable<FileInfo> {
    const headerParams = new HttpParams()
      .append('fileDetailId', fileDetailId)
      .append('domain', domain)
      .append('subDomain', subDomain);
    return this.doGet<FileInfoResponse>(
      '/FileCenter/GetFileDetail',
      headerParams
    ).pipe(
      switchMap((response) => {
        return of(this.convertFileInfoResponseToFileInfo(response));
      })
    );
  }

  /**
   * @description 取得檔案資訊(多檔)
   *
   * @param fileDetailIds 多筆檔案序號
   * @returns 多筆檔案資訊
   */
  getFileDetails(fileDetailIds: string[]): Observable<FileInfo[]> {
    return this.doPost<FileInfoResponse[]>(
      '/FileCenter/PostReturnFileDetailList',
      fileDetailIds
    ).pipe(
      switchMap((response) => {
        return of(
          response.map((file) => {
            return this.convertFileInfoResponseToFileInfo(file);
          })
        );
      })
    );
  }

  /**
   * @description 檔案資訊更新
   *
   * @param putData 要更新的檔案資料,包含 fileDetailId, fileName, description, editor
   * @returns Empty, status code 200 表示成功
   */
  updateFileDetail(putData: {
    fileDetailId: string;
    fileName: string;
    description: string;
    editor: string;
  }): Observable<any> {
    return this.doPut('/FileCenter/PutFiles', putData);
  }

  /**
   * @description 刪除檔案
   *
   * @param fileDetailId 檔案序號
   * @returns Empty, status code 200 表示成功
   */
  deleteFile(fileDetailId: string): Observable<any> {
    const headerParams = new HttpParams().append('fileDetailId', fileDetailId);
    return this.doDelete('/FileCenter/DeleteFile', headerParams);
  }

  private convertDateStringToDate(value: string): Date | undefined {
    try {
      return new Date(value);
    } catch (error) {
      console.error('file center api is return incorrect date format.');
      return undefined;
    }
  }

  private convertFileInfoResponseToFileInfo(
    response: FileInfoResponse
  ): FileInfo {
    const lastDownLoadTime = response.lastDownLoadTime
      ? this.convertDateStringToDate(response.lastDownLoadTime)
      : undefined;
    const createTime = response.createTime
      ? this.convertDateStringToDate(response.createTime)
      : undefined;
    const editTime = response.editTime
      ? this.convertDateStringToDate(response.editTime)
      : undefined;

    return {
      fileDetailId: response.fileDetailId,
      domain: response.domain,
      subDomain: response.subDomain,
      fileName: response.fileName,
      extension: response.extension,
      fullPath: response.fullPath,
      userSideName: response.userSideName,
      description: response.description,
      mimeType: response.mimeType,
      fileSize: response.fileSize,
      sort: response.sort,
      isTemp: response.isTemp,
      lastDownLoadTime,
      createTime: createTime || new Date(),
      creator: response.creator,
      editTime,
      editor: response.editor,
    };
  }
}
